import React from 'react';
import { Pencil, Trash2, ExternalLink, GripVertical } from 'lucide-react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const PortfolioTable = ({ data, onEdit, onDelete, onReorder, canEdit, canDelete, canReorder }) => {
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(data);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);
    onReorder(items);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50/50">
              <TableHead className="w-[40px]"></TableHead>
              <TableHead className="w-[80px]">Image</TableHead>
              <TableHead>Project Info</TableHead>
              <TableHead>Tags</TableHead>
              <TableHead>Industry/Goal</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <Droppable droppableId="portfolio-droppable">
            {(provided) => (
              <TableBody {...provided.droppableProps} ref={provided.innerRef}>
                {data.map((item, index) => (
                  <Draggable key={item.id.toString()} draggableId={item.id.toString()} index={index} isDragDisabled={!canReorder}>
                    {(provided, snapshot) => (
                      <TableRow 
                        ref={provided.innerRef} 
                        {...provided.draggableProps}
                        style={{
                          ...provided.draggableProps.style,
                          display: snapshot.isDragging ? 'table' : 'table-row',
                          tableLayout: 'fixed',
                          backgroundColor: snapshot.isDragging ? '#f8fafc' : 'transparent',
                        }}
                        className="hover:bg-slate-50/50 transition-colors"
                      >
                        <TableCell className="w-[40px]">
                          {canReorder && <div
                            {...provided.dragHandleProps}
                            className="cursor-grab p-1 text-slate-400 hover:text-slate-600 active:cursor-grabbing"
                          >
                            <GripVertical size={16} />
                          </div>}
                        </TableCell>
                        <TableCell>
                          <div className="w-12 h-12 rounded-lg overflow-hidden bg-slate-100 border border-slate-200">
                            <img src={item.image_url} alt={item.name} className="w-full h-full object-cover" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col">
                            <span className="font-semibold text-slate-900">{item.name}</span>
                            {item.link && (
                              <a 
                                href={item.link} 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="text-xs text-primary hover:underline flex items-center gap-1 mt-1 font-medium"
                              >
                                View Site <ExternalLink size={10} />
                              </a>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1 max-w-[200px]">
                            {item.tags?.map((tag, i) => (
                              <Badge key={i} variant="outline" className="text-[10px] px-1.5 py-0 bg-slate-50">
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <span className="text-xs font-medium text-primary bg-primary/10 px-2 py-0.5 rounded-full w-fit">
                              {item.industry_name}
                            </span>
                            <span className="text-[10px] text-slate-400">
                              {item.revenue_goal_name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            {canEdit && <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onEdit(item)}
                              className="text-slate-500 hover:text-primary hover:bg-primary/10 h-8 w-8 p-0"
                            >
                              <Pencil size={16} />
                            </Button>}
                            {canDelete && <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(item.id)}
                              className="text-slate-500 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                            >
                              <Trash2 size={16} />
                            </Button>}
                          </div>
                        </TableCell>
                      </TableRow>
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
                {data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="h-32 text-center text-slate-500">
                      No portfolio items found.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            )}
          </Droppable>
        </Table>
      </div>
    </DragDropContext>
  );
};

export default PortfolioTable;
